import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { StatusBadge } from "@/components/shared/status-badge";
import { MailIcon } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SandboxedHtml } from "@/components/shared/sandboxed-html";
import type { EmailLog } from "@db/schema";

export default function EmailLogsPage() {
  const [search, setSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

  const { data: emailLogs, isLoading } = useQuery<EmailLog[]>({
    queryKey: ["/api/billing/email-logs"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const filteredLogs = useMemo(() => {
    if (!emailLogs) return [];
    if (!search.trim()) return emailLogs;
    const term = search.toLowerCase();
    return emailLogs.filter(
      (log) =>
        log.subject.toLowerCase().includes(term) ||
        log.toEmails.some((email) => email.toLowerCase().includes(term)) ||
        log.templateKey.toLowerCase().includes(term),
    );
  }, [emailLogs, search]);

  const columns: ColumnDef<EmailLog>[] = [
    {
      key: "to",
      header: "To",
      cell: (row) => (
        <span className="text-sm truncate max-w-[200px] block">
          {row.toEmails.join(", ")}
        </span>
      ),
    },
    {
      key: "subject",
      header: "Subject",
      cell: (row) => (
        <span className="font-medium text-sm truncate max-w-[250px] block">
          {row.subject}
        </span>
      ),
    },
    {
      key: "template",
      header: "Template",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.templateKey.replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "delivery",
      header: "Delivery",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.deliveryChannel} / {row.provider ?? "unknown"}
        </span>
      ),
    },
    {
      key: "mode",
      header: "Mode",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">{row.sendMode}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => <StatusBadge type="email" status={row.status} />,
    },
    {
      key: "sentAt",
      header: "Sent At",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {formatDateTime(row.sentAt)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Email Logs"
        description="View all outbound email activity"
      />

      <DataTable
        columns={columns}
        data={filteredLogs}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => setSelectedLog(row)}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by subject or recipient..."
        emptyIcon={MailIcon}
        emptyTitle="No email logs"
        emptyDescription={
          search
            ? "No emails match your search. Try a different term."
            : "No emails have been sent yet."
        }
      />

      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{selectedLog?.subject}</DialogTitle>
            <DialogDescription>
              {selectedLog?.templateKey} · {selectedLog?.sendMode} · {selectedLog?.deliveryChannel}
            </DialogDescription>
          </DialogHeader>
          {selectedLog ? (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-4">
                <div className="rounded-md border p-3 text-sm">
                  <div className="font-medium">Preheader</div>
                  <div>{selectedLog.preheader || "—"}</div>
                </div>
                <div className="rounded-md border p-3 text-sm">
                  <div className="font-medium">Recipients</div>
                  <div>{selectedLog.toEmails.join(", ")}</div>
                </div>
                <div className="rounded-md border p-3 text-sm">
                  <div className="font-medium">Text body</div>
                  <pre className="mt-2 whitespace-pre-wrap">
                    {selectedLog.textContent || "—"}
                  </pre>
                </div>
              </div>
              <div className="rounded-md border p-3">
                {selectedLog.htmlContent ? (
                  <SandboxedHtml
                    html={selectedLog.htmlContent}
                    className="max-h-[640px] overflow-auto"
                  />
                ) : (
                  <div className="text-sm text-muted-foreground">
                    No HTML content stored for this email.
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
